import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updatePost, updatePostHashtag } from "../../../store/campaignBuilderSlice";
import { getPostAssetType, getPostMediaList, hasRenderablePostMedia, MediaSuggestion, MediaSuggestionsTitle, PostTypeHeader } from "../../shared/PostCard";
import Input from "./Input";
import PostPreviewSkeletons from "./PostPreviewSkeletons";
import "bootstrap-icons/font/bootstrap-icons.css";

const PostPreview = ({ readOnly = false }) => {
    const dispatch = useDispatch();
    const { posts, postsLoading, postsError, ideasLoading, publishedCampaign } = useSelector(
        (state) => state.campaignBuilder
    );

    const copyText = async (text, message) => {
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);
            toast(message);
        } catch {
            toast.error("Unable to copy right now");
        }
    };

    if (postsLoading || ideasLoading) {
        return <PostPreviewSkeletons count={Math.max(posts.length, 3)} />;
    }

    if (postsError) {
        return (
            <div className="col-md-12">
                <div className="mx-1 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 md:mx-2 md:p-6">
                    <p className="font-semibold">Could not generate posts</p>
                    <p className="mt-1 text-sm">
                        {typeof postsError === "string" ? postsError : "Something went wrong. Go back and try again."}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="col-md-12">
            <h4 className="mx-1 mb-3 flex items-center gap-2 text-xl font-bold text-slate-900 md:mx-2 md:mb-4 md:gap-3 md:text-2xl">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700 md:h-10 md:w-10">
                    <i className="bi bi-rocket-takeoff text-lg md:text-xl" aria-hidden />
                </span>
                Save Campaign
            </h4>
            <p className="mx-1 mb-4 text-sm leading-relaxed text-slate-600 md:mx-2 md:text-base">
                {posts.length} post{posts.length !== 1 ? "s" : ""} generated — review, tweak if needed, then publish your campaign
            </p>

            {publishedCampaign ? (
                <div className="mx-1 mb-4 rounded-xl border border-green-200 bg-green-50 p-4 text-green-800 md:mx-2">
                    <p className="mb-0 text-sm font-semibold md:text-base">Campaign published successfully!</p>
                    <p className="mb-0 mt-1 text-sm">Redirecting to your campaign…</p>
                </div>
            ) : null}
            <div className="step-container mx-0 py-1 md:mx-2">
                <div className="wrapper">
                    <div className="space-y-3 px-0 md:space-y-4 md:px-1">
                        {posts.length === 0 ? (
                            <p className="text-sm text-slate-500 md:text-base">No posts yet. Go back and try generating again.</p>
                        ) : null}
                        {posts.map((post, idx) => (
                            <div className="post-box p-3.5 md:p-5" key={post.id ?? idx}>
                                <div className="mb-3 md:mb-4">
                                    <PostTypeHeader assetType={getPostAssetType(post)} />
                                </div>

                                <label className="mb-1.5 block text-xs font-semibold text-slate-700 md:text-sm">
                                    Caption
                                </label>
                                <Input
                                    as="textarea"
                                    className="mb-4 !text-sm !leading-relaxed"
                                    rows="10"
                                    value={post.caption}
                                    disabled={readOnly}
                                    onChange={(e) => {
                                        if (readOnly) return;
                                        dispatch(updatePost({ index: idx, field: "caption", value: e.target.value }));
                                    }}
                                />

                                <label className="mb-1.5 block text-xs font-semibold text-slate-700 md:text-sm">
                                    Hashtags
                                </label>
                                <div className="post-hashtags-inputs mb-4 flex flex-wrap gap-2">
                                    {(post.hash_tags || []).map((tag, tagIdx) => (
                                        <Input
                                            key={tagIdx}
                                            type="text"
                                            className="text-base"
                                            value={tag}
                                            disabled={readOnly}
                                            onChange={(e) => {
                                                if (readOnly) return;
                                                dispatch(
                                                    updatePostHashtag({
                                                        postIndex: idx,
                                                        hashtagIndex: tagIdx,
                                                        value: e.target.value,
                                                    })
                                                );
                                            }}
                                        />
                                    ))}
                                </div>

                                {hasRenderablePostMedia(post.media) ? (
                                    <div className="mt-1">
                                        <p className="mb-1.5 text-xs font-semibold text-slate-700 md:text-sm">
                                            <MediaSuggestionsTitle
                                                mediaList={getPostMediaList(post.media)}
                                            />
                                        </p>
                                        <MediaSuggestion
                                            media={post.media}
                                            onCopy={copyText}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PostPreview;
